import { addDays, differenceInCalendarDays, format, parseISO, startOfDay } from "date-fns";
import { buildBaasStablesActivitySnapshot } from "../Stables/baasStablesMetrics";
import { normalizeProcessingDateRange } from "./baasHomeProcessingDateFilters";

const EMPTY_SERIES = { labels: [], primary: [], secondary: [] };

/** Preferred display order for known stables; unknown assets follow alphabetically. */
const STABLE_ASSET_ORDER = ["BRL1", "USDC", "USDT", "EURC", "BRZ"];

export const inProcessingDateRange = (iso, dateRange) => {
  const { dateFrom, dateTo } = normalizeProcessingDateRange(dateRange);
  if (!iso || !dateFrom || !dateTo) return true;

  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return true;

  const start = startOfDay(dateFrom).getTime();
  const end = new Date(dateTo);
  end.setHours(23, 59, 59, 999);
  return ts >= start && ts <= end.getTime();
};

const parseDayKey = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  try {
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      return format(parseISO(raw.slice(0, 10)), "yyyy-MM-dd");
    }
    const asDate = new Date(raw);
    if (!Number.isNaN(asDate.getTime())) {
      return format(asDate, "yyyy-MM-dd");
    }
  } catch {
    return null;
  }
  return null;
};

const buildDayBuckets = (dateRange) => {
  const { dateFrom, dateTo } = normalizeProcessingDateRange(dateRange);
  if (!dateFrom || !dateTo) return new Map();

  const start = startOfDay(dateFrom);
  const end = startOfDay(dateTo);
  const dayCount = Math.max(1, differenceInCalendarDays(end, start) + 1);
  const cappedDays = Math.min(dayCount, 31);
  const byDay = new Map();

  for (let i = 0; i < cappedDays; i += 1) {
    const key = format(addDays(start, i), "yyyy-MM-dd");
    byDay.set(key, { primary: 0, secondary: 0 });
  }

  return byDay;
};

const seriesFromBuckets = (byDay) => {
  if (!byDay.size) return { ...EMPTY_SERIES };
  const days = [...byDay.entries()].map(([key, values]) => ({
    key,
    label: format(parseISO(key), "dd/MM"),
    ...values,
  }));
  return {
    labels: days.map((day) => day.label),
    primary: days.map((day) => day.primary),
    secondary: days.map((day) => day.secondary),
  };
};

const sortStableAssets = (assets) =>
  [...assets].sort((a, b) => {
    const ia = STABLE_ASSET_ORDER.indexOf(a);
    const ib = STABLE_ASSET_ORDER.indexOf(b);
    if (ia >= 0 && ib >= 0) return ia - ib;
    if (ia >= 0) return -1;
    if (ib >= 0) return 1;
    return a.localeCompare(b);
  });

const resolveTxAmount = (tx) => {
  const candidates = [
    tx?.amountStable,
    tx?.amount,
    tx?.amountFrom,
    tx?.amountTo,
    tx?.netToAmount,
    tx?.fromAmount,
  ];
  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
};

const resolveTxAssets = (tx) => {
  const assets = new Set();
  const push = (value) => {
    const asset = String(value || "").trim().toUpperCase();
    if (asset) assets.add(asset);
  };
  push(tx?.asset);
  push(tx?.from);
  push(tx?.to);
  push(tx?.fromAssetId);
  push(tx?.toAssetId);
  push(tx?.source?.asset);
  push(tx?.destination?.asset);
  return [...assets];
};

const isFailedStatus = (status) => {
  const value = String(status || "").toLowerCase();
  return value.includes("fail") || value.includes("falh") || value.includes("cancel");
};

/** Prefer API volume_over_time; fall back to synthetic split of totals. */
export const buildPixDailySeries = (pixMetrics, dateRange) => {
  const labels = pixMetrics?.volumeOverTime?.labels;
  const values = pixMetrics?.volumeOverTime?.values;

  if (Array.isArray(labels) && Array.isArray(values) && labels.length && values.length) {
    const received = Number(pixMetrics?.receivedVolume ?? 0);
    const sent = Number(pixMetrics?.sentVolume ?? 0);
    const total = received + sent;
    const receivedShare = total > 0 ? received / total : 0.5;

    return {
      labels: labels.map((label) => {
        const key = parseDayKey(label);
        return key ? format(parseISO(key), "dd/MM") : String(label);
      }),
      primary: values.map((value) => Number(value || 0) * receivedShare),
      secondary: values.map((value) => Number(value || 0) * (1 - receivedShare)),
    };
  }

  const byDay = buildDayBuckets(dateRange);
  if (!byDay.size) {
    const trend = Array.isArray(pixMetrics?.trendVolume) ? pixMetrics.trendVolume : [];
    if (!trend.length) return { ...EMPTY_SERIES };
    return {
      labels: trend.map((_, index) => String(index + 1)),
      primary: trend.map((value) => Number(value || 0) * 0.55),
      secondary: trend.map((value) => Number(value || 0) * 0.45),
    };
  }

  return seriesFromBuckets(byDay);
};

/**
 * Aggregate stables summary for all stable assets present in the period
 * (BRL1, USDC, USDT, EURC, …) — not limited to USDC × USDT.
 */
export const aggregateStablesSummary = (transactions, dateRange) => {
  const rows = (transactions || []).filter((tx) =>
    inProcessingDateRange(
      tx.createdAt || tx.processedAt || tx.raw?.created_at || tx.raw?.createdAt,
      dateRange,
    ),
  );
  const activity = buildBaasStablesActivitySnapshot(rows);
  const volumeByAsset = { ...(activity.volumeByAsset || {}) };

  const assets = sortStableAssets(
    Object.keys(volumeByAsset).filter((asset) => Number(volumeByAsset[asset] || 0) > 0),
  );

  const { dateFrom, dateTo } = normalizeProcessingDateRange(dateRange);
  const byDay = new Map();
  if (dateFrom && dateTo) {
    const start = startOfDay(dateFrom);
    const end = startOfDay(dateTo);
    const dayCount = Math.max(1, differenceInCalendarDays(end, start) + 1);
    const cappedDays = Math.min(dayCount, 31);
    for (let i = 0; i < cappedDays; i += 1) {
      const key = format(addDays(start, i), "yyyy-MM-dd");
      byDay.set(
        key,
        Object.fromEntries(assets.map((asset) => [asset, 0])),
      );
    }
  }

  rows.forEach((tx) => {
    if (isFailedStatus(tx?.status)) return;

    const dayKey = parseDayKey(
      tx.createdAt || tx.processedAt || tx.raw?.created_at || tx.raw?.createdAt,
    );
    if (!dayKey || !byDay.has(dayKey)) return;

    const amount = resolveTxAmount(tx);
    if (amount <= 0) return;

    let txAssets = resolveTxAssets(tx).filter((asset) => assets.includes(asset));
    if (!txAssets.length) {
      const fallback = String(tx?.asset || "").trim().toUpperCase();
      if (fallback && assets.includes(fallback)) txAssets = [fallback];
    }
    if (!txAssets.length) return;

    const bucket = byDay.get(dayKey);
    txAssets.forEach((asset) => {
      bucket[asset] = (bucket[asset] || 0) + amount;
    });
  });

  const days = [...byDay.entries()].map(([key, values]) => ({
    key,
    label: format(parseISO(key), "dd/MM"),
    ...values,
  }));

  const dailyByAsset = Object.fromEntries(
    assets.map((asset) => [asset, days.map((day) => Number(day[asset] || 0))]),
  );

  const tokens = assets.map((asset) => ({
    asset,
    volume: Number(volumeByAsset[asset] || 0),
  }));

  return {
    assets,
    tokens,
    volumeByAsset,
    volumeUsdc: volumeByAsset.USDC ?? 0,
    volumeUsdt: volumeByAsset.USDT ?? 0,
    volumeBrl1: volumeByAsset.BRL1 ?? 0,
    txCount: activity.total,
    successRate: activity.successRate,
    dailySeries: {
      labels: days.map((day) => day.label),
      byAsset: dailyByAsset,
      // Keep dual-series shape for any legacy consumers.
      primary: dailyByAsset.USDC || days.map(() => 0),
      secondary: dailyByAsset.USDT || days.map(() => 0),
    },
  };
};

export const aggregateEfxSummary = (historyItems, dateRange) => {
  const rows = (historyItems || []).filter((item) =>
    inProcessingDateRange(item.raw?.createdAt || item.createdAt, dateRange),
  );

  let volume = 0;
  let inbound = 0;
  let outbound = 0;
  let failed = 0;
  const byDay = buildDayBuckets(dateRange);

  rows.forEach((item) => {
    const amountMajor = Number(item.fromAmount ?? item.senderAmount ?? 0) || 0;
    volume += amountMajor;
    if (String(item.type || "").toLowerCase() === "payin") {
      inbound += amountMajor;
    } else {
      outbound += amountMajor;
    }
    if (String(item.status || "").toLowerCase() === "failed") failed += 1;

    const dayKey = parseDayKey(item.raw?.createdAt || item.createdAt);
    if (!dayKey || !byDay.has(dayKey)) return;
    const bucket = byDay.get(dayKey);
    if (String(item.type || "").toLowerCase() === "payin") {
      bucket.primary += amountMajor;
    } else {
      bucket.secondary += amountMajor;
    }
  });

  const total = rows.length;
  return {
    volume,
    inbound,
    outbound,
    txCount: total,
    successRate: total > 0 ? ((total - failed) / total) * 100 : 100,
    primaryPair: "USD/BRL",
    displayCurrency: "BRL",
    dailySeries: seriesFromBuckets(byDay),
  };
};

export const formatPeriodLabel = (dateRange) => {
  const { dateFrom, dateTo } = normalizeProcessingDateRange(dateRange);
  if (!dateFrom || !dateTo) return "";
  try {
    const start = format(dateFrom, "dd/MM/yyyy");
    const end = format(dateTo, "dd/MM/yyyy");
    return start === end ? start : `${start} – ${end}`;
  } catch {
    return "";
  }
};
